import { useState } from "react";
import type { components } from "../../../apis/gen";
import { HeatMapLite } from "../../../inJoy-components/3DHeapMap/HeatMapLite";
import { VideoThumbNailCard } from "../../../inJoy-components/Card/VideoThumbNailCard";
import { SegmentedControl } from "../../../inJoy-components/SegmentedControl/SegmentedControl";

type Event = components["schemas"]["Event"];

interface ResultDetailExtended {
	replay_url?: string;
	team_id?: number;
	field_goal_result?: string;
	field_goal_position?: string;
	location?: [number, number, number];
}

interface SessionSummaryTabContentProps {
	event: Event;
	selectedSessionIdx: number;
}

/**
 * Format started_at and ended_at into display string with duration
 * Format: "2025-10-28 Tue・1:00 PM (1 hour)"
 */
const formatDateTimeWithDuration = (
	startedAt: string,
	endedAt?: string,
): string => {
	if (!startedAt) return "N/A";

	const startDate = new Date(startedAt);

	// Format date: "2025-10-28 Tue"
	const year = startDate.getFullYear();
	const month = (startDate.getMonth() + 1).toString().padStart(2, "0");
	const day = startDate.getDate().toString().padStart(2, "0");
	const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	const dayOfWeek = daysOfWeek[startDate.getDay()];
	const dateStr = `${year}-${month}-${day} ${dayOfWeek}`;

	// Format time: "1:00 PM"
	const hours = startDate.getHours();
	const minutes = startDate.getMinutes();
	const ampm = hours >= 12 ? "PM" : "AM";
	const displayHours = hours % 12 || 12;
	const displayMinutes = minutes.toString().padStart(2, "0");
	const timeStr = `${displayHours}:${displayMinutes} ${ampm}`;

	// Calculate duration if ended_at is provided
	let durationStr = "";
	if (endedAt) {
		const endDate = new Date(endedAt);
		const durationMs = endDate.getTime() - startDate.getTime();
		const durationMinutes = Math.round(durationMs / 60000);

		if (durationMinutes >= 60) {
			const hrs = Math.floor(durationMinutes / 60);
			const mins = durationMinutes % 60;
			if (mins > 0) {
				durationStr = ` (${hrs} hr ${mins} min)`;
			} else {
				durationStr = ` (${hrs} hour${hrs > 1 ? "s" : ""})`;
			}
		} else {
			durationStr = ` (${durationMinutes} min)`;
		}
	}

	return `${dateStr}・${timeStr}${durationStr}`;
};

const SessionSummaryTabContent = ({
	event,
	selectedSessionIdx,
}: SessionSummaryTabContentProps) => {
	const [activeChartTab, setActiveChartTab] = useState(0);
	const selectedSession = event.sessions?.[selectedSessionIdx];

	if (!selectedSession) {
		return null;
	}

	// Derive team names from session team_mapping with fallback
	const teamMapping = selectedSession?.team_mapping;
	const teamAName = teamMapping?.["0"] || "Team A";
	const teamBName = teamMapping?.["1"] || "Team B";

	const resultDetails = (selectedSession.result_details ||
		[]) as ResultDetailExtended[];
	const team0Results = resultDetails.filter(
		(result) => Number(result.team_id) === 0,
	);
	const team1Results = resultDetails.filter(
		(result) => Number(result.team_id) === 1,
	);

	return (
		<div className="mt-5 px-5 lg:px-[calc(50vw-600px)] pb-3 lg:pb-[80px] flex flex-col gap-5">
			{/* Highlights for Selected Session */}
			{selectedSession.highlights?.length > 0 && (
				<div className="px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
					<p className="text-2xl font-medium text-color-Grey900">
						Highlights -{" "}
						{selectedSession.name || `Game #${selectedSessionIdx + 1}`}
					</p>
					<div className="mt-4 overflow-x-scroll flex gap-x-1 no-scrollbar">
						{selectedSession.highlights.map((highlight, hIndex) => (
							<VideoThumbNailCard
								key={`highlight-video-${selectedSessionIdx}-${hIndex}`}
								type="overlay"
								rounded={true}
								title={highlight.name}
								videoUrl={highlight.video_url}
								poster={`${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_placeholder_thumbnail.png`}
								sharelink={selectedSession.sharelink || undefined}
								className="w-[300px]"
							/>
						))}
					</div>
				</div>
			)}

			{/* Shot Charts - only show when there are results */}
			{resultDetails.length > 0 && (
				<div className="px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
					<p className="text-2xl font-medium text-color-Grey900">Shot Charts</p>
					{/* Mobile tab switcher */}
					<div className="mt-4 lg:hidden">
						<SegmentedControl
							tabs={[teamAName, teamBName]}
							defaultIndex={activeChartTab}
							onChange={(index) => setActiveChartTab(index)}
						/>
					</div>
					{/* Mobile: show only selected team */}
					<div className="mt-4 lg:hidden">
						<div className="flex-1 flex flex-col">
							<HeatMapLite
								shots={activeChartTab === 0 ? team0Results : team1Results}
								containerHeight={310}
							/>
						</div>
					</div>
					{/* Desktop: side-by-side layout */}
					<div className="mt-4 hidden lg:flex lg:flex-row gap-x-6">
						<div className="flex-1 flex flex-col">
							<div className="pb-2 flex items-center gap-x-2 border-b border-color-Grey300">
								<div className="w-10 h-10 flex items-center justify-center rounded-xl overflow-hidden">
									<img
										src="/img/event-page/team_light.svg"
										alt={teamAName}
										className="w-10 h-10"
									/>
								</div>
								<p className="text-xl font-medium">{teamAName}</p>
							</div>
							<div className="flex gap-x-4"></div>
							<HeatMapLite shots={team0Results} containerHeight={555} />
						</div>
						<div className="flex-1 flex flex-col">
							<div className="pb-2 flex items-center gap-x-2 border-b border-color-Grey300">
								<div className="w-10 h-10 flex items-center justify-center rounded-xl overflow-hidden">
									<img
										src="/img/event-page/team_dark.svg"
										alt={teamBName}
										className="w-10 h-10"
									/>
								</div>
								<p className="text-xl font-medium">{teamBName}</p>
							</div>
							<div className="flex gap-x-4"></div>
							<HeatMapLite shots={team1Results} containerHeight={555} />
						</div>
					</div>
				</div>
			)}

			{/* Game Info for Selected Session */}
			<div className="flex flex-col lg:flex-row gap-5">
				{selectedSession.highlights.length === 0 &&
					resultDetails.length === 0 && (
						<div className="flex-[173] flex flex-col items-center justify-center py-6 px-5 bg-color-Grey50 rounded-lg border border-color-Grey100">
							<img
								className="hidden lg:block w-[240px]"
								src={`${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_info_thumbnail.png`}
								alt=""
							/>
							<p className="text-lg font-semibold text-color-Grey900">
								No info for this game yet
							</p>
							<p className="mt-2 max-w-[411px] text-sm text-color-Grey700 text-center">
								Stay tuned! Once the game video is uploaded, all key insights
								will be automatically analyzed and updated here.
							</p>
						</div>
					)}
				<div className="flex-[100] px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
					<p className="text-2xl font-medium text-color-Grey900">Game Info</p>
					<p className="mt-6 text-sm font-medium text-color-Grey500">
						Event Title
					</p>
					<p className="mt-1 text-base text-color-Grey900">{event.name}</p>
					<p className="mt-4 text-sm font-medium text-color-Grey500">
						Event Date
					</p>
					<p className="mt-1 text-base text-color-Grey900">
						{formatDateTimeWithDuration(
							event.started_at,
							event.ended_at ?? undefined,
						)}
					</p>
					<p className="mt-4 text-sm font-medium text-color-Grey500">
						Participants
					</p>
					<p className="mt-1 text-base text-color-Grey900">
						{event.participants.length} people
					</p>
					{/* {selectedSession.started_at && (
						<>
							<p className="mt-4 text-sm font-medium text-color-Grey500">
								Game Start Time
							</p>
							<p className="mt-1 text-base text-color-Grey900">
								{new Date(selectedSession.started_at).toLocaleString()}
							</p>
						</>
					)}
					{selectedSession.ended_at && (
						<>
							<p className="mt-4 text-sm font-medium text-color-Grey500">
								Game End Time
							</p>
							<p className="mt-1 text-base text-color-Grey900">
								{new Date(selectedSession.ended_at).toLocaleString()}
							</p>
						</>
					)}
					{selectedSession.desc && (
						<>
							<p className="mt-4 text-sm font-medium text-color-Grey500">
								Description
							</p>
							<p className="mt-1 text-base text-color-Grey900">
								{selectedSession.desc}
							</p>
						</>
					)} */}
					{event.location && (
						<>
							{" "}
							<p className="mt-4 text-sm font-medium text-color-Grey500">
								Location
							</p>
							<p className="mt-1 text-base text-color-Grey900 underline">
								{event.location}
							</p>
							<p className="mt-1 text-base text-color-Grey700">
								106 Taipei City, Daan District, Dunnan Street, Lane 76, No. 28
							</p>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export { SessionSummaryTabContent };
